import React, { useEffect, useState } from "react";
import { getProductTypesApi } from "../../../../api/ProductTypeList";
import { deleteProductTypeApi } from "../../../../api/ProductTypeList";
import { Link } from "react-router-dom";
import {
  DeleteOutlined,
  FormOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Modal, Button } from "react-bootstrap";

const ProductTypeList = ({
  searchKeyword = "",
  sortBy = "",
  sortOrder = "asc",
  currentPage = 1,
  onPageChange,
}) => {
  const [productTypes, setProductTypes] = useState([]);
  const [filteredProductTypes, setFilteredProductTypes] = useState([]);
  const [productsPerPage] = useState(10);
  const [deleteId, setDeleteId] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort data whenever filters change
  useEffect(() => {
    let filtered = [...productTypes];

    // Apply search filter
    if (searchKeyword.trim()) {
      filtered = filtered.filter(
        (productType) =>
          productType.name
            .toLowerCase()
            .includes(searchKeyword.toLowerCase()) ||
          productType.categoryID
            .toLowerCase()
            .includes(searchKeyword.toLowerCase()) ||
          (productType.parentCategoryID &&
            productType.parentCategoryID
              .toLowerCase()
              .includes(searchKeyword.toLowerCase()))
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (sortBy === "name") {
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
        } else if (sortBy === "categoryID") {
          aValue = a.categoryID.toLowerCase();
          bValue = b.categoryID.toLowerCase();
        }

        if (sortOrder === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    setFilteredProductTypes(filtered);
  }, [productTypes, searchKeyword, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      const response = await getProductTypesApi();
      setProductTypes(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Lấy chỉ số sản phẩm đầu và cuối của trang hiện tại
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProductType = filteredProductTypes.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  // Chuyển đổi trang
  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleDeleteItem = async () => {
    try {
      await deleteProductTypeApi(deleteId);

      window.location.reload();
      setShow(false);
    } catch (error) {
      console.log("API delete error:", error);
      setShow(false);
    }
  };

  const handleClickDelete = (id) => {
    setDeleteId(id);
    setShow(true);
  };

  const totalPages = Math.ceil(filteredProductTypes.length / productsPerPage);

  return (
    <div>
      {/* Delete Confirmation Modal */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Xóa loại sản phẩm này?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Bạn có chắc muốn xóa loại sản phẩm này không?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteItem}>
            Đồng ý
          </Button>
          <Button variant="primary" onClick={handleClose}>
            Hủy
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Results Info */}
      {filteredProductTypes.length > 0 && (
        <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
          Hiển thị {indexOfFirstProduct + 1} -{" "}
          {Math.min(indexOfLastProduct, filteredProductTypes.length)} trong tổng
          số {filteredProductTypes.length} loại sản phẩm
          {searchKeyword && (
            <span className="ml-2 text-blue-600">
              (tìm kiếm: "{searchKeyword}")
            </span>
          )}
        </div>
      )}

      {/* Table Section */}
      {currentProductType.length > 0 ? (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  STT
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mã Sản Phẩm
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tên Sản Phẩm
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nhóm Sản Phẩm
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Hành Động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentProductType.map((productType, index) => (
                <tr key={productType._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {indexOfFirstProduct + index + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {productType.categoryID}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={productType.name}>
                      {productType.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {productType.parentCategoryID || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <Link
                        to={`/admin/producttype/edit/${productType.categoryID}`}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Chỉnh sửa loại sản phẩm"
                      >
                        <FormOutlined className="text-lg" />
                      </Link>
                      <button
                        onClick={() =>
                          handleClickDelete(productType.categoryID)
                        }
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Xóa loại sản phẩm"
                      >
                        <DeleteOutlined className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchKeyword
              ? "Không tìm thấy loại sản phẩm"
              : "Không có loại sản phẩm nào"}
          </h3>
          <p className="text-gray-500">
            {searchKeyword
              ? `Không có loại sản phẩm nào phù hợp với từ khóa "${searchKeyword}"`
              : "Hãy thêm loại sản phẩm đầu tiên của bạn"}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            Trang {currentPage} / {totalPages}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            {currentPage > 1 && (
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <LeftOutlined className="mr-1" />
                Trước
              </button>
            )}

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === index + 1
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Next Button */}
            {currentPage < totalPages && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                Sau
                <RightOutlined className="ml-1" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTypeList;
